import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { readAuthSessionClaims } from '@/lib/auth-session-claims';
import {
  activateNativeBootstrapWriteGeneration,
  cacheNativeBootstrapProfile,
  deactivateNativeBootstrapWriteGeneration,
} from '@/lib/mobile/offline-db/bootstrap-cache';

import type { Session } from '@supabase/supabase-js';
import type {
  CachedUserProfile,
  NativeBootstrapWriteGeneration,
} from '@/lib/mobile/offline-db/bootstrap-cache';

/** Own cache-write authority for the currently mounted native auth session. */
export function useNativeBootstrapCacheWriter(
  userId: string | undefined,
  session: Session | null,
  profile: CachedUserProfile | null,
): NativeBootstrapWriteGeneration | null {
  const [writeGeneration, setWriteGeneration] = useState<NativeBootstrapWriteGeneration | null>(
    null,
  );
  const authSessionId = useMemo(() => {
    if (!session || !userId) {
      return null;
    }
    const claims = readAuthSessionClaims(session.access_token);
    return claims?.subject === userId ? claims.sessionId : null;
  }, [session, userId]);
  // The null check must be explicit: with no generation AND no user,
  // `writeGeneration?.ownerUserId === userId` is `undefined === undefined`,
  // which passes and then dereferences null — crashing every signed-out mount.
  const activeGeneration =
    writeGeneration !== null &&
    writeGeneration.ownerUserId === userId &&
    writeGeneration.authSessionId === authSessionId
      ? writeGeneration
      : null;

  useLayoutEffect(() => {
    if (!userId || !authSessionId) {
      return;
    }
    let active = true;
    const generation = activateNativeBootstrapWriteGeneration(userId, authSessionId);
    globalThis.queueMicrotask(() => {
      if (active) {
        setWriteGeneration(generation);
      }
    });
    return () => {
      active = false;
      deactivateNativeBootstrapWriteGeneration(generation);
    };
  }, [authSessionId, userId]);

  useEffect(() => {
    if (!userId || !profile || !activeGeneration) {
      return;
    }
    void cacheNativeBootstrapProfile(userId, profile, activeGeneration);
  }, [activeGeneration, profile, userId]);

  return activeGeneration;
}
